import React, { ChangeEventHandler, FocusEventHandler, KeyboardEventHandler, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelectRenderer } from 'react-dropdown-select';
import { useDispatch, useSelector } from 'react-redux';
import orderBy from 'lodash/orderBy';
import styled from 'styled-components';
import { CreatableEnterAction, creatableItemRenderer, CreatableSelect, ICreatableSelectOption } from 'components/CreatableSelect/CreatableSelect';
import { LoaderDots } from 'components/Loader';
import { theme, toRem } from 'design';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getJobTitlesRequest, JobTitles, resetJobTitles, selectJobTitlesBySite } from 'store/Collections';
import { IJobTitleState, IStore } from 'store/types';
import { formatJobTitle } from 'utils/jobs';

interface IProps {
  className?: string;
  /**
   * the text used for the create option in the dropdown.
   *
   * to include the search term in the text, use `{search}`.
   *
   * @example `add {search} as a new job title`
   * @default `add {search}`
   */
  createNewLabel?: string;
  dataCy?: string;
  defaultValue?: string | ICreatableSelectOption<IJobTitleState>;
  disabled?: boolean;
  includeInactive?: boolean;
  placeholder?: string;
  siteId: string;
  onChange(selectedOption: ICreatableSelectOption<IJobTitleState> | undefined): void;
  onInputBlur?: FocusEventHandler<HTMLInputElement>;
  onInputChange?: ChangeEventHandler<HTMLInputElement>;
  onInputClick?: MouseEventHandler;
  onInputFocus?: FocusEventHandler<HTMLInputElement>;
  onInputKeypress?: KeyboardEventHandler<HTMLInputElement>;
  /**
   * a callback function to be used for validating a
   * newly created job title
   *
   * @return [boolean] true if the new item is valid.
   * false if not.
   */
  validator?(newItem: ICreatableSelectOption<IJobTitleState> | undefined): boolean;
  filterOptions?: string[];
  loadOptions?: boolean;
  customOptions?: IJobTitleState[];
  onNewJobTitle?(newJob: IJobTitleState): void;
  selectId?: string;
  tooltipId?: string;
}

const AddNewOption = styled.div`
  &:hover {
    background-color: ${theme.color.neutral[100]};
  }
`;

const NoData = styled.div`
  padding: ${toRem(8)} ${toRem(13.75)};
  color: ${theme.color.neutral[800]};
  cursor: auto;

  .loader {
    margin: ${toRem(6)} auto;
  }
`;

const DropdownContainer = styled.div`
  display: block;
  overflow: auto;
`;

const OptionsContainer = styled.div`
  display: block;
  text-transform: capitalize;
`;

/**
 * CreatableSelect will only return an option
 * with the value set as a string (the value of the
 * newly entered text). Need to manually parse it
 * into a ICreatableSelectOption<IJobTitleState>
 * here.
 */
const parseJobTitleOption = (option: ICreatableSelectOption<string | IJobTitleState> | undefined): ICreatableSelectOption<IJobTitleState> | undefined => {
  if (!option) return undefined;

  if ((option as any)['value.title']) {
    const parsed = {
      ...option,
      value: {
        title: (option as any)['value.title'],
      },
    };
    delete (parsed as any)['value.title'];
    return parsed;
  }

  if (typeof option.value !== 'string') return option as ICreatableSelectOption<IJobTitleState>;

  return {
    ...option,
    value: {
      title: option.value,
    },
  };
};

export const JobTitleCreatableSelect: React.FC<IProps> = ({
  className = '',
  createNewLabel,
  dataCy,
  defaultValue,
  disabled,
  includeInactive,
  placeholder,
  siteId,
  onChange,
  onInputBlur,
  onInputChange,
  onInputClick,
  onInputFocus,
  onInputKeypress,
  validator,
  filterOptions = null,
  loadOptions = true,
  customOptions,
  onNewJobTitle,
  selectId,
  tooltipId,
}) => {
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const [selected, setSelected] = useState<ICreatableSelectOption<IJobTitleState>>(null);
  const [options, setOptions] = useState<ICreatableSelectOption<IJobTitleState>[]>([]);
  const isLoadingJobs = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, JobTitles.GET_JOB_TITLES_REQUEST));
  const jobTitles = customOptions || useSelector(selectJobTitlesBySite(siteId));
  const [disableCreationOnEnter, setDisableCreationOnEnter] = useState(false);

  useEffect(
    () => () => {
      if (!filterOptions) {
        dispatch(resetJobTitles());
      }
    },
    []
  );

  useEffect(() => {
    if (!siteId || !loadOptions) return;
    dispatch(getJobTitlesRequest({ siteId, includeInactive }));
  }, [siteId, loadOptions]);

  useEffect(() => {
    if (!jobTitles || !isMounted.current) return;
    if (
      defaultValue &&
      ((typeof defaultValue === 'string' && selected?.value?.title === defaultValue) || (typeof defaultValue === 'object' && selected?.value?.title === (defaultValue as ICreatableSelectOption<IJobTitleState>)?.value?.title))
    )
      return;

    onChange(parseJobTitleOption(selected));
  }, [selected]);

  useEffect(() => {
    if (!jobTitles) return;

    const opts = orderBy(
      jobTitles.map((jobTitle) => ({
        label: formatJobTitle(jobTitle.title),
        value: jobTitle,
      })),
      [(job) => job.label.toLowerCase()],
      ['asc']
    );

    setOptions(opts);

    if (!isMounted.current && defaultValue) {
      const defaultTitle = typeof defaultValue === 'string' ? defaultValue : defaultValue.value?.title;

      if (defaultTitle) {
        const normalizedTitle = defaultTitle.toLocaleLowerCase();
        const defaultOption: ICreatableSelectOption<IJobTitleState> = opts.find((opt) => opt.value.title.toLocaleLowerCase() === normalizedTitle);

        if (defaultOption) {
          setSelected(parseJobTitleOption({ ...defaultOption, value: defaultTitle }));
        } else {
          const newItem = parseJobTitleOption({ value: defaultTitle, label: formatJobTitle(defaultTitle) });
          setSelected(newItem);
          setOptions([...opts, newItem]);
        }
      }
    }
    isMounted.current = true;
  }, [jobTitles]);

  const filteredOptions = useMemo(() => options.filter((jobTitle) => !filterOptions?.some((opt) => opt.toLocaleLowerCase() === jobTitle?.value.title.toLocaleLowerCase())), [options, filterOptions]);

  const onEnterPress = useCallback(
    ({ state, props }: SelectRenderer<ICreatableSelectOption<IJobTitleState>> & { inputRef: React.RefObject<HTMLInputElement> }) => {
      if (typeof state.cursor === 'number') {
        return { action: CreatableEnterAction.Select, optionToSelect: state.values[state.cursor] };
      }

      const exactMatch = props.options.find((value) => value.value.title.toLocaleLowerCase() === state.search.toLocaleLowerCase());

      if (exactMatch) {
        return { action: CreatableEnterAction.Select, optionToSelect: exactMatch };
      }
      if (state.search.trim() && !disableCreationOnEnter) {
        return { action: CreatableEnterAction.Create };
      }

      return { action: CreatableEnterAction.None };
    },
    [disableCreationOnEnter]
  );

  const onJobTitleChange = useCallback((items: ICreatableSelectOption<IJobTitleState>[]) => {
    setSelected(items[0]);
  }, []);

  const onJobTitleCreate = useCallback(
    (newItem: ICreatableSelectOption<string>) => {
      let parsedNewItem = parseJobTitleOption(newItem);
      const isValid = validator ? validator(parsedNewItem) : true;

      if (!isValid || !parsedNewItem) {
        setOptions([...options]);
        return;
      }

      parsedNewItem = { ...parsedNewItem, label: formatJobTitle(parsedNewItem.label) };

      setOptions([...options, parsedNewItem]);
      setSelected(parsedNewItem);
      onNewJobTitle?.({ title: parsedNewItem.label });
    },
    [validator, options]
  );

  const onJobTitleInputChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (!e.target.value) {
        setSelected(undefined);
      }

      onInputChange?.(e);
    },
    [onInputChange]
  );

  const renderDropdown = useCallback(
    ({ props, state, methods }: SelectRenderer<ICreatableSelectOption<IJobTitleState>>) => {
      const regexp = new RegExp(state.search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');

      // overrides default search function
      const searchResults = props.options.filter((item) => regexp.test(item.label)).filter((item) => props.keepSelectedInList || !methods.isSelected(item));

      const existingOption = searchResults?.find((option) => option.value.title.toLocaleLowerCase() === state.search.toLocaleLowerCase());
      let addNewOptionComp: JSX.Element;
      let noDataOptionComp: JSX.Element;

      const search = state.search.trim().replace(/\s+/g, ' ');
      const excluded = filterOptions?.some((opt) => opt.replace(/\s+/g, ' ').toLocaleLowerCase() === search.toLocaleLowerCase());

      /*
       * only include the add new option if the search text
       * does not match Exactly the title of one of the existing
       * options, if the user has entered valid characters into
       * the search AND if it has not been excluded.
       */
      if (!existingOption && search && !excluded) {
        setDisableCreationOnEnter(false);
        const onAddNewClick: MouseEventHandler = (e) => {
          e.preventDefault();
          methods.createNew(search);
        };

        const searchLabel = `"${state.search}"`;
        const parsedCreateNewLabel = createNewLabel ? createNewLabel.replaceAll('{search}', () => searchLabel) : `Add ${searchLabel}`;

        addNewOptionComp = (
          <AddNewOption onClick={onAddNewClick} className="react-dropdown-select-dropdown-add-new" data-cy="add-new-option">
            {parsedCreateNewLabel}
          </AddNewOption>
        );
      } else {
        setDisableCreationOnEnter(true);
      }

      const optionsComps = searchResults?.map((item, itemIndex) => creatableItemRenderer({ item, itemIndex, methods, state, props }));

      if (isLoadingJobs) {
        noDataOptionComp = (
          <NoData>
            <LoaderDots className="loader" size="small" color={theme.color.secondary[400]} />
          </NoData>
        );
      } else if (excluded) {
        noDataOptionComp = <NoData>Job title is not allowed.</NoData>;
      } else if (!search && optionsComps.length === 0) {
        noDataOptionComp = <NoData>No jobs available. Type to create a new one.</NoData>;
      }

      return (
        <DropdownContainer>
          {noDataOptionComp || addNewOptionComp}
          <OptionsContainer>{!isLoadingJobs && optionsComps}</OptionsContainer>
        </DropdownContainer>
      );
    },
    [createNewLabel, filterOptions, isLoadingJobs]
  );

  return (
    <CreatableSelect
      backspaceDelete={false}
      className={className}
      dataCy={dataCy}
      disabled={disabled}
      disableCreationOnEnter={disableCreationOnEnter}
      dropdownRenderer={renderDropdown}
      inputProps={{ maxLength: 40, autoComplete: 'off' }}
      onChange={onJobTitleChange}
      onCreate={onJobTitleCreate}
      onEnterPress={onEnterPress}
      onInputBlur={onInputBlur}
      onInputChange={onJobTitleInputChange}
      onInputClick={onInputClick}
      onInputFocus={onInputFocus}
      onInputKeypress={onInputKeypress}
      options={filteredOptions}
      placeholder={placeholder}
      values={[selected]}
      selectId={selectId}
      data-tooltip-id={tooltipId}
    />
  );
};
